import type { EdgeApiNameType } from '@/constants';

// --- Site services (single source: edge `capabilityId`, optional device `pickerId`) ---

/**
 * One row per cluster capability. `pickerId` = device-picker / stored id; omit for cluster-only
 * capabilities (hybrid power, Orbit One).
 */
export const SITE_SERVICES = {
  BARGE_CONTROL: { capabilityId: 'bargecontrolApi', pickerId: 'BARGE_CONTROL' },
  CAMERA: { capabilityId: 'cameracontrolApi', pickerId: 'camera' },
  GENERATOR: { capabilityId: 'generatorApi', pickerId: 'generator' },
  GIO: { capabilityId: 'gioApi', pickerId: 'gio' },
  ORBIT_SENSOR: { capabilityId: 'orbitApi', pickerId: 'orbitSensor' },
  ORBIT_LED: { capabilityId: 'orbitLedApi', pickerId: 'orbitLed' },
  WATERLINKED: { capabilityId: 'waterlinkedApi', pickerId: 'waterlinked' },
  MORT_HANDLING: { capabilityId: 'morthandlingApi', pickerId: 'mortHandling' },
  FEEDING: { capabilityId: 'feedingApi' },
  HYBRID_POWER: { capabilityId: 'hybridPowerApi' },
  ORBIT_ONE: { capabilityId: 'orbitoneApi' },
} as const;

type SiteKey = keyof typeof SITE_SERVICES;

/** Stored / compared picker service ids (subset of rows that have `pickerId`). */
export type DeviceServiceName = {
  [K in SiteKey]: 'pickerId' extends keyof (typeof SITE_SERVICES)[K]
    ? (typeof SITE_SERVICES)[K]['pickerId']
    : never;
}[SiteKey];

export type NonBargeDeviceServiceName = Exclude<
  DeviceServiceName,
  (typeof SITE_SERVICES)['BARGE_CONTROL']['pickerId']
>;

/** Edge API id per logical capability (pods, widgets, strict set). */
export const CLUSTER_CAPABILITIES = Object.fromEntries(
  (Object.keys(SITE_SERVICES) as SiteKey[]).map(key => [key, SITE_SERVICES[key].capabilityId])
) as {
  readonly [K in SiteKey]: (typeof SITE_SERVICES)[K]['capabilityId'];
} satisfies { readonly [key: string]: EdgeApiNameType };

export type ClusterCapabilityId = (typeof CLUSTER_CAPABILITIES)[keyof typeof CLUSTER_CAPABILITIES];

/** Device-config add-device picker: which service rows are enabled for this cluster. */
export type SiteDeviceServicesAvailability = {
  availableServices: readonly DeviceServiceName[];
};
